import { Header, Section, VStack } from 'tio-ui/components/layout';
import SearchInput from 'tio-ui/components/search-input';
import Select from 'tio-ui/components/select';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import Component from '@glimmer/component';
import PslfAdminCompanyFormsTable from 'tio-employee/components/pslf/admin/company-forms-table';
import type { IntlService } from 'ember-intl';
import type { PslfForm } from 'tio-data/data/resources/search.ext';
import type { SearchModelWith } from 'tio-data/data/resources/search.ext';
import type AdminPslfFormsController from 'tio-employee/controllers/authenticated/admin/pslf/forms';
import type RouterService from '@ember/routing/router-service';
import type SessionContextService from 'tio-employee/services/session-context';
import { PSLF_ADMIN_STATUS_FILTERS } from 'tio-employee/utils/pslf/admin-status-filters';

export interface AdminPslfFormsRouteSignature {
  Args: {
    controller: AdminPslfFormsController;
    model: SearchModelWith<PslfForm>;
  };
}

export default class AdminPslfFormsRoute extends Component<AdminPslfFormsRouteSignature> {
  @service declare sessionContext: SessionContextService;
  @service declare intl: IntlService;
  @service declare router: RouterService;

  formTypes = ['currentEmployer', 'priorEmployer'];

  statusFilterItems = PSLF_ADMIN_STATUS_FILTERS.map((status) => {
    return {
      value: this.intl.t(status.labelKey),
      key: status.key,
    };
  });

  get approverGroupIds() {
    return this.sessionContext.currentEmployee?.approverPslfGroupIds || [];
  }

  get groupFilterItems() {
    const groups = this.isGroupApprover
      ? this.groups.filter((group) => group.id && this.approverGroupIds.includes(group.id))
      : this.groups;

    return groups.map((group) => {
      return {
        value: group.employerLegalName,
        key: group.id,
      };
    });
  }

  formTypeFilterItems = this.formTypes.map((type) => {
    const formType = type === 'currentEmployer' ? 'current_employer' : 'prior_employer';
    return {
      value: this.intl.t(`pslf.forms.${formType}`),
      key: type,
    };
  });

  get company() {
    return this.sessionContext.currentCompany;
  }

  get groups() {
    return this.company?.pslfGroups || [];
  }

  get isGroupApprover() {
    return this.sessionContext.isPslfGroupApprover;
  }

  <template>
    <VStack>
      <Header data-test-page-title>{{t "pslf.forms.index_page_title"}}</Header>
      <Section>
        <:body>
          <div class="grid grid-cols-4 gap-4">
            <SearchInput
              data-test-search-input
              @placeholder="Search (Name, Email)"
              @value={{@controller.search}}
              @onChange={{fn (mut @controller.search)}}
              @onSearch={{fn (mut @controller.search)}}
            />
            <Select
              data-test-group-filter
              @clearable={{true}}
              @placeholder="By group"
              @items={{this.groupFilterItems}}
              @selected={{@controller.group}}
              @onChange={{fn (mut @controller.group)}}
            />
            <Select
              data-test-status-filter
              @clearable={{true}}
              @placeholder="By status"
              @items={{this.statusFilterItems}}
              @selected={{@controller.status}}
              @onChange={{fn (mut @controller.status)}}
            />
            {{#unless this.isGroupApprover}}
              <Select
                data-test-form-type-filter
                @clearable={{true}}
                @placeholder="By form type"
                @items={{this.formTypeFilterItems}}
                @selected={{@controller.formType}}
                @onChange={{fn (mut @controller.formType)}}
              />
            {{/unless}}
          </div>
        </:body>
      </Section>
      <Section class="overflow-auto">
        <:body>
          <PslfAdminCompanyFormsTable @model={{@model}} @controller={{@controller}} />
        </:body>
      </Section>
    </VStack>
  </template>
}
